import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime, pName } from "@/lib/format";
import { ScrollText, X } from "lucide-react";
import { ProductCombobox } from "@/components/ProductCombobox";

const TYPES = ["receipt", "sale", "wholesale", "transfer_out", "transfer_in", "adjustment", "return", "write_off"];
const TYPE_COLOR = {
  receipt: "text-emerald-600", transfer_in: "text-emerald-600",
  sale: "text-rose-600", wholesale: "text-rose-600", transfer_out: "text-rose-600", write_off: "text-rose-600",
  adjustment: "text-amber-600", return: "text-blue-600",
};

export default function Ledger() {
  const { branches } = useBranch();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [branch, setBranch] = useState(params.get("branch_id") || "all");
  const [product, setProduct] = useState(params.get("product_id") || "all");
  const [type, setType] = useState("all");

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const p = { limit: 500 };
    if (branch !== "all") p.branch_id = branch;
    if (product !== "all") p.product_id = product;
    if (type !== "all") p.type = type;
    api.get("/stock/movements", { params: p }).then((r) => setRows(r.data)).catch(() => {});
  }, [branch, product, type]);
  useEffect(() => { load(); }, [load]);

  const clear = () => { setBranch("all"); setProduct("all"); setType("all"); setParams({}); };
  const selectedProduct = products.find((p) => p.id === product);

  return (
    <div>
      <PageHeader title="Stock movement ledger" subtitle="Every receipt, sale, transfer, adjustment and write-off — trace a discrepancy to its source" />
      <Card className="p-3 mb-4 flex flex-wrap items-end gap-3 shadow-sm">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Branch</label>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="ledger-branch" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Medicine</label>
          <div className="flex gap-2">
            <ProductCombobox products={products} value={product} onChange={setProduct} allLabel="All medicines" testId="ledger-product" className="w-72" />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="ledger-type" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="ghost" onClick={clear} data-testid="ledger-clear"><X className="h-4 w-4 mr-1" /> Clear</Button>
        <div className="ml-auto text-xs text-muted-foreground self-center">{rows.length} movement{rows.length === 1 ? "" : "s"}</div>
      </Card>

      {selectedProduct && (
        <div className="mb-3 text-sm text-muted-foreground" data-testid="ledger-context">
          Showing <span className="font-medium text-foreground">{pName(selectedProduct)}</span>
          {branch !== "all" && <> at <span className="font-medium text-foreground">{branches.find((b) => b.id === branch)?.name}</span></>}
        </div>
      )}

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">When</th><th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3">Medicine</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3">Ref</th><th className="py-2.5 px-3">By</th><th className="py-2.5 px-3">Note</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />No movements match these filters</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-border hover:bg-muted/50" data-testid={`ledger-row-${m.id}`}>
                <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(m.timestamp)}</td>
                <td className="py-2 px-3"><span className={`text-xs font-semibold uppercase ${TYPE_COLOR[m.type] || "text-muted-foreground"}`}>{(m.type || "").replace(/_/g, " ")}</span></td>
                <td className="py-2 px-3 font-medium text-foreground">{m.generic_name}<span className="text-xs text-muted-foreground ml-1">{m.sku}</span></td>
                <td className="py-2 px-3 text-muted-foreground">{m.branch_name}</td>
                <td className={`py-2 px-3 text-right font-mono font-medium ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</td>
                <td className="py-2 px-3 text-xs"><span className="uppercase text-muted-foreground">{m.ref_type}</span></td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{m.actor_name}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{m.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
